import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
  useTheme,
  alpha,
  Checkbox,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { useNotification } from '../../../contexts/NotificationContext';
import { useGetAllAdminUsersQuery } from '../../../hooks/useGetAllAdminUsersQuery';
import { useCreateAdminUserMutation } from '../../../hooks/useCreateAdminUserMutation';
import { useUpdateAdminUserMutation } from '../../../hooks/useUpdateAdminUserMutation';
import { useDeleteAdminUserMutation } from '../../../hooks/useDeleteAdminUserMutation';
import { useResetAdminPasswordMutation } from '../../../hooks/useResetAdminPasswordMutation';
import { useUserContext } from '../../../hooks/userContext';

const AdminUserManagement: React.FC = () => {
  const [adminUsers, setAdminUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'Admin',
    permissions: [] as string[],
  });
  const { showSuccess, showError } = useNotification();
  const theme = useTheme();
  
  // GraphQL hooks
  const { getAllAdminUsers, loading: queryLoading, error: queryError } = useGetAllAdminUsersQuery();
  const { createAdminUser, loading: createLoading } = useCreateAdminUserMutation();
  const { updateAdminUser, loading: updateLoading } = useUpdateAdminUserMutation();
  const { deleteAdminUser, loading: deleteLoading } = useDeleteAdminUserMutation();
  const { resetAdminPassword, loading: resetLoading } = useResetAdminPasswordMutation();
  const { user } = useUserContext();

  const roles = ['SuperAdmin', 'Admin', 'Moderator'];
  const permissions = [
    'UserManagement',
    'ContentManagement',
    'SystemSettings',
    'Analytics',
    'Reports',
    'All',
  ];

  // Load admin users on component mount
  useEffect(() => {
    const loadAdminUsers = async () => {
      try {
        const users = await getAllAdminUsers(true);
        setAdminUsers(users);
      } catch (error) {
        showError('Failed to load admin users');
      }
    };
    loadAdminUsers();
  }, [showError]); // Removed getAllAdminUsers from dependencies to prevent infinite loop

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: 'Admin',
      permissions: [],
    });
    setOpenDialog(true);
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      password: '',
      role: user.role,
      permissions: user.permissions,
    });
    setOpenDialog(true);
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this admin user?')) {
      try {
        await deleteAdminUser(userId);
        showSuccess('Admin user deleted successfully');
        // Refresh the list
        const users = await getAllAdminUsers(true);
        setAdminUsers(users);
      } catch (error) {
        showError('Failed to delete admin user');
      }
    }
  };

  const handleResetPassword = async (userId: string) => {
    const newPassword = prompt('Enter new password:');
    if (newPassword && newPassword.trim()) {
      try {
        const requesterEmail = user?.email || undefined; // Get email from logged-in user for authentication
        const success = await resetAdminPassword(userId, newPassword.trim(), requesterEmail);
        
        if (success) {
          showSuccess('Password reset successfully');
        } else {
          showError('Password reset failed - no success response');
        }
      } catch (error) {
        showError(`Failed to reset password: ${error instanceof Error ? error.message : String(error)}`);
      }
    } else if (newPassword !== null) {
      showError('Password cannot be empty');
    }
  };

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        await updateAdminUser({
          _id: editingUser._id,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          role: formData.role,
          permissions: formData.permissions,
        });
        showSuccess('Admin user updated successfully');
      } else {
        await createAdminUser({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          permissions: formData.permissions,
        });
        showSuccess('Admin user created successfully');
      }
      setOpenDialog(false);
      // Refresh the list
      const users = await getAllAdminUsers(true);
      setAdminUsers(users);
    } catch (error) {
      showError('Failed to save admin user');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'SuperAdmin': return 'error';
      case 'Admin': return 'primary';
      case 'Moderator': return 'secondary';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Admin User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateUser}
        >
          Create Admin User
        </Button>
      </Box>

      {queryError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load admin users: {queryError.message}
        </Alert>
      )}


      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            '&::-webkit-scrollbar': {
              height: 6,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.grey[100],
              borderRadius: 3,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.grey[400],
              borderRadius: 3,
              '&:hover': {
                backgroundColor: theme.palette.grey[600],
              },
            },
          }}
        >
          <Table 
            className="admin-dashboard-table" 
            size="small"
            sx={{
              minWidth: { xs: 800, md: 'auto' },
              "& .MuiTableCell-root": {
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                whiteSpace: { xs: 'nowrap', md: 'normal' },
              },
              "& .MuiTableHead-root .MuiTableCell-root": {
                fontSize: { xs: '0.75rem', md: '0.875rem' },
              },
            }}
          >
          <TableHead className="header">
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={false}
                  checked={false}
                  onChange={() => {}}
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {adminUsers.map((user) => (
              <TableRow key={user._id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={false}
                    onChange={() => {}}
                  />
                </TableCell>
                <TableCell>
                  {user.firstName} {user.lastName}
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip 
                    label={user.role} 
                    color={getRoleColor(user.role) as any}
                    size="small"
                    sx={{
                      fontSize: '0.75rem',
                      height: 24,
                      py: 0.5, // 8px
                      px: 0.5, // 8px
                      minWidth: 'auto',
                      width: 'fit-content',
                      borderRadius: 50,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {user.permissions.map((permission: string) => (
                      <Chip
                        key={permission}
                        label={permission}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '0.75rem',
                          height: 24,
                          py: 0.5, // 8px
                      px: 0.5, // 8px
                      minWidth: 'auto',
                      width: 'fit-content',
                      borderRadius: 50,
                        }}
                      />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.isActive ? 'Active' : 'Inactive'}
                    color={user.isActive ? 'success' : 'default'}
                    size="small"
                    sx={{
                      fontSize: '0.75rem',
                      height: 24,
                      py: 0.5, // 8px
                      px: 0.5, // 8px
                      minWidth: 'auto',
                      width: 'fit-content',
                      borderRadius: 50,
                    }}
                  />
                </TableCell>
                <TableCell>
                  N/A
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="Edit User">
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                        sx={{
                          width: 28,
                          height: 28,
                          backgroundColor: 'transparent',
                          color: 'rgba(0, 0, 0, 0.8)',
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        <EditIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Reset Password">
                      <IconButton
                        size="small"
                        onClick={() => handleResetPassword(user._id)}
                        sx={{
                          width: 28,
                          height: 28,
                          backgroundColor: 'transparent',
                          color: 'rgba(0, 0, 0, 0.8)',
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.warning.main, 0.04),
                            color: theme.palette.warning.main,
                          },
                        }}
                      >
                        <LockResetIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete User">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteUser(user._id)}
                        sx={{
                          width: 28,
                          height: 28,
                          backgroundColor: 'transparent',
                          color: 'rgba(0, 0, 0, 0.8)',
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.error.main, 0.04),
                            color: theme.palette.error.main,
                          },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit Admin User' : 'Create Admin User'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
            </Stack>
            
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            
            <TextField
              fullWidth
              label={editingUser ? "New Password (leave blank to keep current)" : "Password"}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!editingUser}
            />
            
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300, // Fixed height for 10 items (30px per item)
                      overflow: 'auto',
                    },
                  },
                }}
              >
                {roles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Permissions</InputLabel>
              <Select
                multiple
                value={formData.permissions}
                onChange={(e) => setFormData({ ...formData, permissions: e.target.value as string[] })}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300, // Fixed height for 10 items (30px per item)
                      overflow: 'auto',
                    },
                  },
                }}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Stack>
                )}
              >
                {permissions.map((permission) => (
                  <MenuItem key={permission} value={permission}>
                    {permission}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveUser} 
            variant="contained"
            disabled={createLoading || updateLoading}
          >
            {(createLoading || updateLoading) ? <CircularProgress size={20} sx={{ color: '#00A06D' }} /> : (editingUser ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUserManagement;
